import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import UnfoldLess from "@mui/icons-material/UnfoldLess";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { SuggestedField } from "components/ArtificialIntelligence/SuggestedField";
import { SuggestedStatePicker } from "components/ArtificialIntelligence/SuggestedStatePicker";
import { SuggestedTextField } from "components/ArtificialIntelligence/SuggestedTextField";
import { safeParseFloat, safeParseInt } from "encamp-shared/src/tierii/utilities";
import { useState } from "react";

import type { useManifestReviewSection } from "../hooks/useManifestReviewSection";

type ManifestDetailSectionProps = {
  manifest: ReturnType<typeof useManifestReviewSection>;
};

type ManifestSection = ReturnType<typeof useManifestReviewSection>;

export function ManifestDetailSection({ manifest }: ManifestDetailSectionProps) {
  if (manifest.manifestDataLoaded) {
    return (
      <Box sx={{ p: 1 }}>
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {manifest.manifestNeedsReview ? (
              <>
                AI parsed this manifest document. Please review the parsed values{" "}
                <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                  (marked with <SparkleIcon fontSize="inherit" sx={{ verticalAlign: "middle" }} />)
                </Box>{" "}
                and confirm.
              </>
            ) : (
              "This is the raw data parsed from the manifest document. Edits here will update the waste manifest record and trigger reprocessing."
            )}
          </Typography>

          {/* Manifest-level fields */}
          <Typography variant="subtitle1" fontWeight="bold">
            Hazardous Waste Manifest
          </Typography>
          <SuggestedTextField
            name="manifestTrackingNumber"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Tracking Number"
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="shippedDate"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Shipped Date"
              type="date"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <SuggestedTextField
              name="receivedDate"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Received Date"
              type="date"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          {manifest.trackingNumberChanged && (
            <Alert severity="warning" variant="outlined">
              Changing the tracking number will move this manifest data to a different processed
              manifest record.
            </Alert>
          )}

          {/* Generator */}
          <Typography variant="subtitle1" fontWeight="bold">
            Generator
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorId"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="EPA ID"
              size="small"
              sx={{ maxWidth: 200 }}
            />
            <SuggestedTextField
              name="generatorName"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Name"
              size="small"
              fullWidth
            />
          </Stack>
          <SuggestedTextField
            name="generatorMailStreet1"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Mailing Street"
            size="small"
            fullWidth
          />
          <SuggestedTextField
            name="generatorMailStreet2"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Mailing Street 2"
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorMailCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              sx={{ flex: 1 }}
            />
            <SuggestedStatePicker
              name="generatorMailState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              sx={{ width: 140, flex: "0 0 auto" }}
            />
            <SuggestedTextField
              name="generatorMailZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ width: 130, flex: "0 0 auto" }}
            />
          </Stack>
          <SuggestedTextField
            name="generatorLocationStreet1"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Site Street"
            size="small"
            fullWidth
          />
          <SuggestedTextField
            name="generatorLocationStreet2"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Site Street 2"
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorLocationCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              sx={{ flex: 1 }}
            />
            <SuggestedStatePicker
              name="generatorLocationState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              sx={{ width: 140, flex: "0 0 auto" }}
            />
            <SuggestedTextField
              name="generatorLocationZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ width: 130, flex: "0 0 auto" }}
            />
          </Stack>

          {/* Destination Facility */}
          <Typography variant="subtitle1" fontWeight="bold">
            Destination Facility (TSDF)
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="desFacilityId"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="EPA ID"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="desFacilityName"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Name"
              size="small"
              fullWidth
            />
          </Stack>
          <SuggestedTextField
            name="desFacLocationStreet1"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Street"
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="desFacLocationCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              sx={{ flex: 1 }}
            />
            <SuggestedStatePicker
              name="desFacLocationState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              sx={{ width: 140, flex: "0 0 auto" }}
            />
            <SuggestedTextField
              name="desFacLocationZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ width: 130, flex: "0 0 auto" }}
            />
          </Stack>

          <WasteLinesSection manifest={manifest} />

          {/* Transporters */}
          <Typography variant="subtitle1" fontWeight="bold">
            Transporters ({manifest.transporterFieldArray.fields.length})
          </Typography>
          {manifest.transporterFieldArray.fields.map((field, index) => (
            <Stack key={field.id} direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">{field.transporterLineNumber}.</Typography>
              <SuggestedTextField
                name={`transporters.${index}.transporterId`}
                control={manifest.manifestControl}
                needsReview={manifest.manifestNeedsReview}
                label="EPA ID"
                size="small"
                sx={{ minWidth: 150 }}
              />
              <SuggestedTextField
                name={`transporters.${index}.transporterName`}
                control={manifest.manifestControl}
                needsReview={manifest.manifestNeedsReview}
                label="Name"
                size="small"
                fullWidth
              />
              <IconButton
                size="small"
                onClick={() => {
                  manifest.onDeleteTransporter(field.transporterLineNumber);
                  manifest.transporterFieldArray.remove(index);
                }}
                title="Remove transporter"
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              const nextLineNumber = String(
                Math.max(
                  0,
                  ...manifest.transporterFieldArray.fields.map(
                    (f) => safeParseInt(f.transporterLineNumber) ?? 0
                  )
                ) + 1
              );
              manifest.transporterFieldArray.append({
                transporterLineNumber: nextLineNumber,
                transporterId: "",
                transporterName: "",
              });
            }}
          >
            Add Transporter
          </Button>
        </Stack>
      </Box>
    );
  }

  if (manifest.manifestLoading) {
    return <Skeleton variant="rectangular" height={200} sx={{ my: 2, gridColumn: "1 / -1" }} />;
  }

  return (
    <Alert severity="info" variant="outlined" sx={{ my: 2, gridColumn: "1 / -1" }}>
      This document has been classified as a waste manifest. Manifest data will be parsed after you
      confirm, and you can review it from the Waste Manifests page.
    </Alert>
  );
}

function WasteLinesSection({ manifest }: { manifest: ManifestSection }) {
  const fieldArray = manifest.wasteLineFieldArray;

  // Lazy init: first row expanded by default. Storing wasteLineNumber (not
  // field.id) so the "Add Waste Line" handler can update the Set in one go
  // — it knows the new wasteLineNumber synchronously.
  const [expandedLineNumbers, setExpandedLineNumbers] = useState<Set<string>>(() => {
    const first = fieldArray.fields[0]?.wasteLineNumber;
    return new Set(first ? [first] : []);
  });

  const setLineExpanded = (lineNumber: string, expanded: boolean) =>
    setExpandedLineNumbers((prev) => {
      const next = new Set(prev);
      if (expanded) next.add(lineNumber);
      else next.delete(lineNumber);
      return next;
    });

  const allExpanded =
    fieldArray.fields.length > 0 &&
    fieldArray.fields.every((f) => expandedLineNumbers.has(f.wasteLineNumber));

  const toggleAll = () =>
    setExpandedLineNumbers(
      allExpanded ? new Set() : new Set(fieldArray.fields.map((f) => f.wasteLineNumber))
    );

  const handleAddLine = () => {
    const nextLineNumber = String(
      Math.max(0, ...fieldArray.fields.map((f) => safeParseInt(f.wasteLineNumber) ?? 0)) + 1
    );
    fieldArray.append({
      wasteLineNumber: nextLineNumber,
      dotHazardous: false,
      dotIdNumberDescription: "",
      dotPrintedInformation: "",
      nonHazWasteDescription: "",
      containerNumber: null,
      containerTypeCode: "",
      quantity: null,
      qtyUnitOfMeasureCode: "",
      managementMethodCode: "",
      federalWasteCodes: "",
      stateWasteCodes: "",
    });
    // Newly-added line starts expanded so the user can fill it in.
    setLineExpanded(nextLineNumber, true);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          Line Items ({fieldArray.fields.length})
        </Typography>
        {fieldArray.fields.length > 1 && (
          <Button
            size="small"
            variant="text"
            startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
            onClick={toggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Stack>
      {fieldArray.fields.map((field, index) => (
        <WasteLineRow
          key={field.id}
          field={field}
          index={index}
          manifest={manifest}
          expanded={expandedLineNumbers.has(field.wasteLineNumber)}
          onToggle={(open) => setLineExpanded(field.wasteLineNumber, open)}
        />
      ))}
      <Button size="small" startIcon={<Add />} onClick={handleAddLine}>
        Add Waste Line
      </Button>
    </>
  );
}

type WasteLineRowProps = {
  field: { id: string; wasteLineNumber: string };
  index: number;
  manifest: ManifestSection;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
};

function WasteLineRow({ field, index, manifest, expanded, onToggle }: WasteLineRowProps) {
  const lineValues = manifest.manifestWatch(`wasteLines.${index}`);
  const isHazardous = lineValues.dotHazardous;
  const description = isHazardous
    ? lineValues.dotPrintedInformation
    : lineValues.nonHazWasteDescription;

  return (
    <Accordion expanded={expanded} onChange={(_, isOpen) => onToggle(isOpen)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Line {field.wasteLineNumber}
          </Typography>
          {isHazardous && (
            <WarningAmber
              color="warning"
              sx={{ fontSize: 18, flexShrink: 0 }}
              titleAccess="Hazardous"
            />
          )}
          {description && (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
              {" — "}
              {description}
            </Typography>
          )}
          {!description && <Box flex={1} />}
          <IconButton
            component="div"
            role="button"
            tabIndex={0}
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              manifest.onDeleteWasteLine(field.wasteLineNumber);
              manifest.wasteLineFieldArray.remove(index);
            }}
            title="Remove waste line"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <SuggestedField
              name={`wasteLines.${index}.dotHazardous`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleIcon }) => (
                <Stack direction="row" alignItems="center">
                  <FormControlLabel
                    control={<Checkbox checked={!!f.value} onChange={f.onChange} />}
                    label="DOT Hazardous"
                  />
                  {isSuggested && sparkleIcon}
                </Stack>
              )}
            </SuggestedField>
            <SuggestedTextField
              name={`wasteLines.${index}.dotIdNumberDescription`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="DOT ID/Description"
              size="small"
              fullWidth
            />
          </Stack>
          <SuggestedTextField
            name={`wasteLines.${index}.dotPrintedInformation`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="DOT Printed Information"
            size="small"
            fullWidth
          />
          <SuggestedTextField
            name={`wasteLines.${index}.nonHazWasteDescription`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Non-Haz Waste Description"
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <SuggestedField
              name={`wasteLines.${index}.containerNumber`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleAdornment }) => (
                <TextField
                  {...f}
                  value={f.value ?? ""}
                  onChange={(e) =>
                    f.onChange(e.target.value === "" ? null : safeParseInt(e.target.value))
                  }
                  label="Container Number"
                  type="number"
                  size="small"
                  sx={{ flex: 1, minWidth: 90 }}
                  slotProps={{
                    input: {
                      startAdornment: isSuggested ? sparkleAdornment : undefined,
                    },
                  }}
                />
              )}
            </SuggestedField>
            <SuggestedTextField
              name={`wasteLines.${index}.containerTypeCode`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Container Type"
              size="small"
              sx={{ flex: 1, minWidth: 110 }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedField
              name={`wasteLines.${index}.quantity`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleAdornment }) => (
                <TextField
                  {...f}
                  value={f.value ?? ""}
                  onChange={(e) =>
                    f.onChange(e.target.value === "" ? null : safeParseFloat(e.target.value))
                  }
                  label="Quantity"
                  type="number"
                  size="small"
                  sx={{ flex: 1, minWidth: 90 }}
                  slotProps={{
                    input: {
                      startAdornment: isSuggested ? sparkleAdornment : undefined,
                    },
                  }}
                />
              )}
            </SuggestedField>
            <SuggestedTextField
              name={`wasteLines.${index}.qtyUnitOfMeasureCode`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Unit of Measure Code"
              size="small"
              sx={{ flex: 1, minWidth: 90 }}
            />
          </Stack>
          <SuggestedTextField
            name={`wasteLines.${index}.managementMethodCode`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Management Method Code"
            size="small"
            fullWidth
          />
          <SuggestedTextField
            name={`wasteLines.${index}.federalWasteCodes`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Federal Waste Codes"
            size="small"
            fullWidth
            helperText="Comma-separated"
          />
          <SuggestedTextField
            name={`wasteLines.${index}.stateWasteCodes`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="State Waste Codes"
            size="small"
            fullWidth
            helperText="Comma-separated"
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
